import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';

import { FormField } from '@/components/shared/form-field';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { OtpInput } from '@/components/ui/otp-input';
import { Spinner } from '@/components/ui/spinner';
import { useSignIn } from '@/hooks/use-auth';
import { useConsoleSessionPolicy } from '@/hooks/use-session-keep-alive';
import { ApiError } from '@/lib/api-errors';
import { captureReferralCodeFromSearch } from '@/lib/referral-link';
import { isSafeReturnTo } from '@/lib/safe-return-to';
import { useAuthStore } from '@/stores/auth-store';

import { FormError } from './form-error';
import { QrLoginPanel } from './qr-login-tab';
import { SignInShell } from './sign-in-shell';
import './auth.css';
import { messageFor } from './validation-message';

const passwordSchema = z.object({
  email: z.string().min(1, 'emailRequired').pipe(z.email('emailInvalid')),
  password: z.string().min(1, 'passwordRequired'),
});

type PasswordValues = z.infer<typeof passwordSchema>;

export const SignInPage = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const status = useAuthStore((state) => state.status);
  const restore = useAuthStore((state) => state.restore);
  const signIn = useSignIn();
  const consoleSession = useConsoleSessionPolicy();
  const rememberMeEnabled = consoleSession.data?.rememberMeEnabled === true;

  useEffect(() => {
    if (status === 'unknown') void restore();
  }, [status, restore]);

  useEffect(() => {
    captureReferralCodeFromSearch(searchParams);
  }, [searchParams]);

  const [rememberMe, setRememberMe] = useState(false);
  const [needsTotp, setNeedsTotp] = useState(false);
  const [totpCode, setTotpCode] = useState('');

  const form = useForm<PasswordValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { email: '', password: '' },
  });

  const from = (location.state as { from?: string } | null)?.from;
  const returnTo = searchParams.get('returnTo');
  const destination = isSafeReturnTo(returnTo)
    ? returnTo
    : from && from !== '/'
      ? from
      : '/home';

  if (status === 'unknown') {
    return (
      <div className="flex min-h-dvh items-center justify-center auth-brand-shell">
        <Spinner className="auth-brand-shell-content size-6 text-on-brand" />
      </div>
    );
  }

  if (status === 'authenticated') {
    return <Navigate to={destination} replace />;
  }

  const submitWithTotp = async (code: string) => {
    const values = form.getValues();
    try {
      await signIn.mutateAsync({
        email: values.email,
        password: values.password,
        totpCode: code,
        rememberMe: rememberMeEnabled && rememberMe,
      });
      void navigate(destination, { replace: true });
    } catch {
      setTotpCode('');
    }
  };

  const submitPassword = form.handleSubmit(async (values) => {
    try {
      await signIn.mutateAsync({
        email: values.email,
        password: values.password,
        rememberMe: rememberMeEnabled && rememberMe,
      });
      void navigate(destination, { replace: true });
    } catch (error) {
      if (error instanceof ApiError && error.code === 'TOTP_REQUIRED') {
        signIn.reset();
        setTotpCode('');
        setNeedsTotp(true);
      }
    }
  });

  const backToPassword = () => {
    setNeedsTotp(false);
    setTotpCode('');
    signIn.reset();
  };

  const totpError =
    signIn.error instanceof ApiError && signIn.error.code !== 'TOTP_REQUIRED'
      ? signIn.error
      : null;

  return (
    <SignInShell
      showQr={!needsTotp}
      form={
        <div className="space-y-5">
          <div>
            <h1 className="text-section-title font-semibold text-fg">
              {needsTotp ? t('signIn.totpTitle') : t('signIn.title')}
            </h1>
            {needsTotp ? (
              <p className="mt-1.5 text-body-sm text-fg-secondary">{t('signIn.totpDescription')}</p>
            ) : null}
          </div>

          {needsTotp ? (
            <form
              className="space-y-4"
              noValidate
              onSubmit={(event) => {
                event.preventDefault();
                if (totpCode.length === 6) void submitWithTotp(totpCode);
              }}
            >
              <OtpInput
                value={totpCode}
                onChange={setTotpCode}
                autoFocus
                disabled={signIn.isPending}
                invalid={Boolean(totpError)}
                aria-label={t('signIn.totpCode')}
                onComplete={(code) => {
                  if (!signIn.isPending) void submitWithTotp(code);
                }}
              />
              <FormError error={totpError} />
              <Button
                type="submit"
                loading={signIn.isPending}
                disabled={totpCode.length !== 6}
                className="h-11 w-full font-semibold"
              >
                {t('signIn.totpSubmit')}
              </Button>
              <div className="text-center">
                <Button type="button" variant="link" size="sm" onClick={backToPassword}>
                  {t('signIn.totpBack')}
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={(event) => void submitPassword(event)} className="space-y-4" noValidate>
              <FormField
                label={t('field.email')}
                required
                error={messageFor(form.formState.errors.email?.message, t)}
              >
                {(field) => (
                  <Input
                    {...field}
                    {...form.register('email')}
                    type="email"
                    autoComplete="email"
                    autoFocus
                  />
                )}
              </FormField>

              <FormField
                label={t('field.password')}
                required
                error={messageFor(form.formState.errors.password?.message, t)}
              >
                {(field) => (
                  <PasswordInput
                    {...field}
                    {...form.register('password')}
                    autoComplete="current-password"
                  />
                )}
              </FormField>

              <FormError error={signIn.error} />

              <div className="flex items-center justify-between gap-3">
                {rememberMeEnabled ? (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="rememberMe"
                      checked={rememberMe}
                      onCheckedChange={(checked) => setRememberMe(checked === true)}
                    />
                    <Label htmlFor="rememberMe" className="cursor-pointer font-normal">
                      {t('signIn.rememberMe')}
                    </Label>
                  </div>
                ) : (
                  <span />
                )}
                <Link to="/forgot-password" className="text-body-sm text-brand hover:underline">
                  {t('signIn.forgotPassword')}
                </Link>
              </div>

              <Button
                type="submit"
                loading={form.formState.isSubmitting}
                className="h-11 w-full font-semibold"
              >
                {t('signIn.submit')}
              </Button>
            </form>
          )}
        </div>
      }
      qr={<QrLoginPanel destination={destination} />}
    />
  );
};
